"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnbondingAccount = void 0;
const associatedToken_1 = require("./../common/associatedToken");
const web3_js_1 = require("@solana/web3.js");
const anchor_1 = require("@project-serum/anchor");
const common_1 = require("../common");
const spl_token_1 = require("@solana/spl-token");
class UnbondingAccount {
    /**
     * Creates an instance of UnbondingAccount.
     * @param {PublicKey} address
     * @param {UnbondingAccountInfo} unbondingAccount
     * @memberof UnbondingAccount
     */
    constructor(program, address, unbondingAccount, stakePool) {
        this.program = program;
        this.address = address;
        this.unbondingAccount = unbondingAccount;
        this.stakePool = stakePool;
        /** The total amount of tokens unbonding */
        this.tokens = new anchor_1.BN(0);
        if (!stakePool.stakePool.unbonding.shares.isZero()) {
            this.tokens = unbondingAccount.shares
                .mul(stakePool.stakePool.unbonding.tokens)
                .div(stakePool.stakePool.unbonding.shares);
        }
        else {
            this.tokens = new anchor_1.BN(0);
        }
    }
    /**
     * TODO:
     * @private
     * @static
     * @param {BN} seed
     * @returns {Buffer}
     * @memberof UnbondingAccount
     */
    static toBuffer(seed) {
        const buffer = Buffer.alloc(4);
        buffer.writeUInt32LE(seed);
        return buffer;
    }
    /**
     * TODO:
     * @static
     * @returns {BN}
     * @memberof UnbondingAccount
     */
    static randomSeed() {
        const min = 0;
        const max = this.UINT32_MAXVALUE;
        return Math.floor(Math.random() * (max - min) + min);
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} program
     * @param {PublicKey} stakeAccount
     * @param {BN} seed
     * @returns {Promise<PublicKey>}
     * @memberof UnbondingAccount
     */
    static deriveUnbondingAccount(program, stakeAccount, seed) {
        return (0, common_1.findDerivedAccount)(program.programId, stakeAccount, this.toBuffer(seed));
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} program
     * @param {PublicKey} stakeAccount
     * @param {BN} seed
     * @param {StakePool} stakePool
     * @returns {Promise<UnbondingAccount>}
     * @memberof UnbondingAccount
     */
    static async load(program, stakeAccount, seed, stakePool) {
        const address = this.deriveUnbondingAccount(program, stakeAccount, seed);
        const unbondingAccount = await program.account.unbondingAccount.fetch(address);
        return new UnbondingAccount(program, address, unbondingAccount, stakePool);
    }
    /**
     * TODO:
     * @static
     * @param {Program<StakeIdl>} program
     * @param {PublicKey} stakeAccount
     * @param {StakePool} stakePool
     * @returns {Promise<UnbondingAccount[]>}
     * @memberof UnbondingAccount
     */
    static async loadByStakeAccount(program, stakeAccount, stakePool) {
        // Filter by UnbondingAccount.stakeAccount
        const stakeAccountFilter = {
            memcmp: {
                offset: 8,
                bytes: stakeAccount.toBase58()
            }
        };
        const unbondingAccounts = await program.account.unbondingAccount.all([stakeAccountFilter]);
        return unbondingAccounts.map(info => new UnbondingAccount(program, info.publicKey, info.account, stakePool));
    }
    /**
     * TODO:
     * @static
     * @param {UnbondingAccount} [unbondingAccount]
     * @returns {boolean}
     * @memberof UnbondingAccount
     */
    static isUnbonded(unbondingAccount) {
        const time = new Date().getTime() / 1000;
        const canWithdraw = time > (0, common_1.bnToNumber)(unbondingAccount === null || unbondingAccount === void 0 ? void 0 : unbondingAccount.unbondingAccount.unbondedAt);
        return canWithdraw;
    }
    /**
     * TODO:
     * @static
     * @param {UnbondingAccount[] | undefined} [unbondingAccounts]
     * @returns {UnbondingAmount}
     * @memberof UnbondingAccount
     */
    static useUnbondingAmountTotal(unbondingAccounts) {
        let unbondingQueue = new anchor_1.BN(0);
        let unbondingComplete = new anchor_1.BN(0);
        if (unbondingAccounts) {
            unbondingQueue = unbondingAccounts.reduce((total, curr) => total.add(UnbondingAccount.isUnbonded(curr) ? new anchor_1.BN(0) : curr.tokens), new anchor_1.BN(0));
            unbondingComplete = unbondingAccounts.reduce((total, curr) => total.add(UnbondingAccount.isUnbonded(curr) ? curr.tokens : new anchor_1.BN(0)), new anchor_1.BN(0));
        }
        return { unbondingQueue, unbondingComplete };
    }
    /**
     * TODO:
     * @static
     * @param {TransactionInstruction[]} instructions
     * @param {StakePool} stakePool
     * @param {StakeAccount} stakeAccount
     * @param {ProgramAccount<TokenOwnerRecord>} tokenOwnerRecord
     * @param {PublicKey} payer
     * @param {BN} unbondingSeed
     * @param {BN} amount
     * @memberof UnbondingAccount
     */
    static async withUnbondStake(instructions, stakePool, stakeAccount, tokenOwnerRecord, payer, unbondingSeed, amount = null) {
        const address = this.deriveUnbondingAccount(stakePool.program, stakeAccount.addresses.stakeAccount, unbondingSeed);
        const ix = await stakePool.program.methods
            .unbondStake(unbondingSeed, amount)
            .accounts({
            owner: stakeAccount.stakeAccount.owner,
            payer,
            stakeAccount: stakeAccount.addresses.stakeAccount,
            stakePool: stakePool.addresses.stakePool,
            stakePoolVault: stakePool.addresses.stakePoolVault,
            unbondingAccount: address,
            voterWeightRecord: stakeAccount.addresses.voterWeightRecord,
            maxVoterWeightRecord: stakePool.stakePool.maxVoterWeightRecord,
            tokenOwnerRecord: tokenOwnerRecord,
            systemProgram: web3_js_1.SystemProgram.programId
        })
            .instruction();
        instructions.push(ix);
    }
    /**
     * TODO:
     *
     * @static
     * @param {TransactionInstruction[]} instructions
     * @param {UnbondingAccount} unbondingAccount
     * @param {StakeAccount} stakeAccount
     * @param {StakePool} stakePool
     * @param {PublicKey} tokenReceiver
     * @param {PublicKey} rentReceiver
     * @memberof UnbondingAccount
     */
    static async withWithdrawUnbonded(instructions, unbondingAccount, stakeAccount, stakePool, tokenReceiver, rentReceiver) {
        const ix = await stakeAccount.program.methods
            .withdrawUnbonded()
            .accounts({
            owner: stakeAccount.stakeAccount.owner,
            closer: rentReceiver,
            tokenReceiver: tokenReceiver,
            stakeAccount: stakeAccount.addresses.stakeAccount,
            stakePool: stakeAccount.stakeAccount.stakePool,
            stakePoolVault: stakePool.addresses.stakePoolVault,
            unbondingAccount: unbondingAccount.address,
            tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
        })
            .instruction();
        instructions.push(ix);
    }
    /**
     * TODO:
     *
     * @static
     * @param {UnbondingAccount} unbondingAccount
     * @param {StakeAccount} stakeAccount
     * @param {StakePool} stakePool
     * @param {Provider} provider
     * @returns {Promise<TransactionInstruction[]>}
     * @memberof UnbondingAccount
     */
    static async withdrawUnbonded(unbondingAccount, stakeAccount, stakePool, provider) {
        const ix = [];
        const tokenReceiver = await associatedToken_1.AssociatedToken.withCreate(ix, provider, stakeAccount.stakeAccount.owner, stakePool.stakePool.tokenMint);
        this.withWithdrawUnbonded(ix, unbondingAccount, stakeAccount, stakePool, tokenReceiver, provider.wallet.publicKey);
        return ix;
    }
    /**
     * TODO:
     *
     * @static
     * @param {TransactionInstruction[]} instructions
     * @param {UnbondingAccount} unbondingAccount
     * @param {StakeAccount} stakeAccount
     * @param {StakePool} stakePool
     * @param {PublicKey} rentReceiver
     * @memberof UnbondingAccount
     */
    static async withCancelUnbond(instructions, unbondingAccount, stakeAccount, stakePool, rentReceiver) {
        const ix = await unbondingAccount.program.methods
            .cancelUnbond()
            .accounts({
            owner: stakeAccount.stakeAccount.owner,
            receiver: rentReceiver,
            stakeAccount: stakeAccount.addresses.stakeAccount,
            voterWeightRecord: stakeAccount.stakeAccount.voterWeightRecord,
            maxVoterWeightRecord: stakePool.stakePool.maxVoterWeightRecord,
            stakePool: stakePool.addresses.stakePool,
            stakePoolVault: stakePool.vault.address,
            unbondingAccount: unbondingAccount.address
        })
            .instruction();
        instructions.push(ix);
    }
    /**
     * TODO:
     *
     * @static
     * @param {UnbondingAccount} unbondingAccount
     * @param {StakeAccount} stakeAccount
     * @param {StakePool} stakePool
     * @param {PublicKey} rentReceiver
     * @returns {TransactionInstruction[]}
     * @memberof UnbondingAccount
     */
    static cancelUnbond(unbondingAccount, stakeAccount, stakePool, rentReceiver) {
        const ix = [];
        this.withCancelUnbond(ix, unbondingAccount, stakeAccount, stakePool, rentReceiver);
        return ix;
    }
}
exports.UnbondingAccount = UnbondingAccount;
UnbondingAccount.UINT32_MAXVALUE = 2 ** 32;
//# sourceMappingURL=unbondingAccount.js.map